import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import { Icon as Iconify } from '@iconify/react'
import { unescapeHtml } from 'utils/htmlSymbolHandling'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { GlassTitle, useEngineeringUi } from 'components/WellPages/Engineering/Utils/EngineeringStyling'
import DataConfigFormItem from 'components/WellList/DataConfigModal/DataConfigFormItem'
import { appColors } from 'utils'
import { numberWithCommasDecimals } from 'utils/stringFunctions'

function toNumberOrNull(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function uniq(arr) {
  const seen = new Set()
  const out = []
  for (const v of arr || []) {
    const s = String(v ?? '').trim()
    if (!s) continue
    if (seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

function buildAlertFromRow(row, defaultAlerts) {
  const base = defaultAlerts?.find((a) => a.name === row.name)
  const phaseArr = uniq(row.phase)
  const rigArr = uniq(row.rig)

  const baseUseMin = Boolean(base?.useMin)
  const baseUseMax = Boolean(base?.useMax)

  return {
    name: row.name,
    severity: row.severity,
    phase: phaseArr.length > 0 ? phaseArr.join('|') : 'All',
    rig: rigArr.length > 0 ? rigArr.join('|') : 'All',
    ignoreProjections: row.ignoreProjections,
    silenceAlert: row.silenceAlert,
    useEmail: row.useEmail,
    useSms: row.useSms,
    useDashboards: row.useDashboards,

    unit: base?.unit,
    uid: base?.uid,
    active: true,

    useMin: baseUseMin,
    useMax: baseUseMax,

    min: baseUseMin ? (toNumberOrNull(row.min) ?? base?.min) : base?.min,
    max: baseUseMax ? (toNumberOrNull(row.max) ?? base?.max) : base?.max,

    useMinDepth: Boolean(row.useMinDepth),
    minDepth: row.useMinDepth ? toNumberOrNull(row.minDepth) : null,
    useMaxDepth: Boolean(row.useMaxDepth),
    maxDepth: row.useMaxDepth ? toNumberOrNull(row.maxDepth) : null,
  }
}

const emptyRow = {
  name: '',
  severity: '',
  phase: [],
  rig: [],
  min: '',
  max: '',
  useMinDepth: false,
  minDepth: '',
  useMaxDepth: false,
  maxDepth: '',
  ignoreProjections: false,
  silenceAlert: false,
  useEmail: false,
  useSms: false,
  useDashboards: false,
}

const AddAlertDialog = ({
  open,
  onClose,
  onSubmit,
  alertTypeDropDowns,
  severityDropDowns,
  phaseDropDowns,
  rigDropDowns,
  defaultAlerts,
  showProjections,
  alertCategory,
  alertToEdit = null,
  smsAvailable = false,
}) => {
  const { theme, getTextColor, getDisabledColor, getCardBackground, getBackColor, getWindowBarColor } = useInnovaTheme()
  const ui = useEngineeringUi(true)

  const inputSx = useMemo(
    () => ({
      ...ui.input,
      width: '100%',
      '& .MuiInputBase-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
    }),
    [ui.input, getBackColor, getTextColor, getDisabledColor, theme],
  )

  const shouldShowDepths = useMemo(() => {
    if (alertCategory === 'Anti-Collision' || alertCategory === 'Drilling Parameters') return true
    return false
  }, [alertCategory])

  const textColor = getTextColor()
  const cardBg = getCardBackground()

  const [rows, setRows] = useState([{ ...emptyRow }])

  function alertToRow(alert) {
    if (!alert) return null

    return {
      name: alert.name || '',
      severity: alert.severity || '',
      phase: alert.phase && alert.phase !== 'All' ? String(alert.phase).split('|').filter(Boolean) : [],
      rig: alert.rig && alert.rig !== 'All' ? String(alert.rig).split('|').filter(Boolean) : [],
      min: alert.min ?? '',
      max: alert.max ?? '',
      useMinDepth: Boolean(alert.useMinDepth),
      minDepth: alert.minDepth ?? '',
      useMaxDepth: Boolean(alert.useMaxDepth),
      maxDepth: alert.maxDepth ?? '',
      ignoreProjections: Boolean(alert.ignoreProjections),
      silenceAlert: Boolean(alert.silenceAlert),
      useEmail: Boolean(alert.useEmail),
      useSms: Boolean(alert.useSms),
      useDashboards: Boolean(alert.useDashboards),
    }
  }

  const defaultSeverity =
    Array.isArray(severityDropDowns) && severityDropDowns.length === 1 ? unescapeHtml(severityDropDowns[0]) : ''

  useEffect(() => {
    if (!open) return

    if (alertToEdit) {
      setRows([alertToRow(alertToEdit)])
      return
    }

    setRows([
      {
        ...emptyRow,
        severity: defaultSeverity,
      },
    ])
  }, [open, defaultSeverity, alertToEdit])

  const canSubmit = useMemo(() => {
    return rows.every((r) => r.name && r.severity)
  }, [rows])

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }

  const handleSubmit = () => {
    const created = rows.map((r) => buildAlertFromRow(r, defaultAlerts))

    if (alertToEdit) {
      onSubmit?.({
        ...alertToEdit,
        ...created[0],
      })
      return
    }

    onSubmit?.(created)
  }

  const autoCompleteSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
      '& .MuiChip-root': {
        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        color: getTextColor(),
      },
      '& .MuiChip-deleteIcon': {
        color: getDisabledColor(),
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          backgroundColor: cardBg,
          color: textColor,
          border: theme === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.10)',
        },
      },
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            backgroundColor: cardBg,
            color: textColor,
            padding: 0,
          },
          '& .MuiAutocomplete-option': {
            color: textColor,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
          },
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
    }),
    [cardBg, textColor, theme],
  )

  const showErrors = useMemo(() => {
    return rows.map((r) => ({
      name: !r.name,
      severity: !r.severity,
    }))
  }, [rows])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '90vh',
        },
      }}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            flex: 1,
          }}>
          <Iconify icon={alertToEdit ? 'mdi:pencil-outline' : 'fluent:add-12-filled'} />
          {alertToEdit ? 'Edit Alert' : 'Add Alert'}
        </Typography>

        <IconButton onClick={onClose} size='small'>
          <Iconify icon='mdi:close' />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 1.5,
          backgroundColor: getBackColor(),
        }}>
        {rows.map((r, idx) => {
          const base = defaultAlerts?.find((a) => a.name === r.name)
          const showMin = Boolean(base?.useMin)
          const showMax = Boolean(base?.useMax)

          return (
            <Box
              key={idx}
              sx={{
                ...ui.surface,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
                minWidth: 0,
              }}>
              <GlassTitle title={'Alert Settings'} ui={ui} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, py: 1 }}>
                {[
                  { label: 'Email', field: 'useEmail' },
                  { label: 'Sms', field: 'useSms' },
                  { label: 'Dashboard', field: 'useDashboards' },
                ].map(({ label, field }) => {
                  const isSmsDisabled = field === 'useSms' && !smsAvailable

                  const checkbox = (
                    <Checkbox
                      size='small'
                      checked={r[field]}
                      disabled={isSmsDisabled}
                      onChange={(e) => {
                        if (isSmsDisabled) return
                        updateRow(idx, {
                          [field]: e.target.checked,
                        })
                      }}
                      sx={{
                        p: 0.25,
                        color: ui.defaultTextColor,
                      }}
                    />
                  )

                  return (
                    <DataConfigFormItem
                      key={field}
                      item={{ label }}
                      columnGap={1}
                      sx={{
                        gridTemplateColumns: 'auto auto',
                        flex: '0 0 auto',
                      }}>
                      {isSmsDisabled ? (
                        <Tooltip
                          arrow
                          placement='bottom'
                          enterDelay={300}
                          title={
                            <Box sx={{ p: 0.75, maxWidth: 260 }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 500,
                                  color: textColor,
                                  mb: 0.75,
                                }}>
                                SMS is unavailable, it can be enabled in the User settings.
                              </Typography>

                              <Box
                                sx={{
                                  borderTop: 1,
                                  borderColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                                  pt: 0.75,
                                }}>
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontStyle: 'italic',
                                    color: theme === 'dark' ? '#429CEB' : 'rgba(0,0,0,0.38)',
                                  }}>
                                  SMS is only available in the US, CA, or UK.
                                </Typography>
                              </Box>
                            </Box>
                          }
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: cardBg,
                                color: textColor,
                                border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'}`,
                                boxShadow:
                                  theme === 'dark' ? '0 4px 14px rgba(0,0,0,0.35)' : '0 4px 14px rgba(0,0,0,0.15)',
                                borderRadius: 1.5,
                                maxWidth: 300,
                              },
                            },
                            arrow: {
                              sx: {
                                color: cardBg,
                                '&::before': {
                                  border: `1px solid ${
                                    theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'
                                  }`,
                                },
                              },
                            },
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, 8],
                                  },
                                },
                              ],
                            },
                          }}>
                          <span style={{ display: 'inline-flex' }}>{checkbox}</span>
                        </Tooltip>
                      ) : (
                        checkbox
                      )}
                    </DataConfigFormItem>
                  )
                })}{' '}
              </Box>

              <Box
                sx={{
                  width: '100%',
                  borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
                  my: 0.5,
                }}
              />

              <GlassTitle title={'Alert Details'} ui={ui} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1,
                  width: '100%',
                }}>
                <DataConfigFormItem item={{ label: 'Alert Type' }} labelWidth={115} controlMaxWidth={340}>
                  <TextField
                    select
                    fullWidth
                    size='small'
                    value={r.name}
                    error={showErrors[idx]?.name}
                    helperText={showErrors[idx]?.name ? 'Required' : ''}
                    onChange={(e) => {
                      const name = unescapeHtml(e.target.value ?? '')
                      const selectedBase = defaultAlerts?.find((a) => a.name === name)

                      updateRow(idx, {
                        name,
                        min: selectedBase?.min ?? '',
                        max: selectedBase?.max ?? '',
                      })
                    }}
                    sx={inputSx}>
                    {(alertTypeDropDowns || []).map((value) => (
                      <MenuItem key={value} value={value}>
                        {unescapeHtml(value)}
                      </MenuItem>
                    ))}
                  </TextField>
                </DataConfigFormItem>

                <DataConfigFormItem item={{ label: 'Severity' }} labelWidth={115} controlMaxWidth={340}>
                  <TextField
                    select
                    fullWidth
                    size='small'
                    value={r.severity}
                    error={showErrors[idx]?.severity}
                    helperText={showErrors[idx]?.severity ? 'Required' : ''}
                    onChange={(e) => {
                      updateRow(idx, {
                        severity: unescapeHtml(e.target.value ?? ''),
                      })
                    }}
                    sx={inputSx}>
                    {(severityDropDowns || []).map((value) => (
                      <MenuItem key={value} value={value}>
                        {unescapeHtml(value)}
                      </MenuItem>
                    ))}
                  </TextField>
                </DataConfigFormItem>

                <DataConfigFormItem item={{ label: 'Phase' }} labelWidth={115} controlMaxWidth={340}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size='small'
                    options={phaseDropDowns || []}
                    value={r.phase}
                    onChange={(_, next) => {
                      updateRow(idx, { phase: uniq(next) })
                    }}
                    sx={{
                      ...autoCompleteSx,
                      minWidth: 200,
                    }}
                    slotProps={autoCompleteSlotProps}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </DataConfigFormItem>

                <DataConfigFormItem item={{ label: 'Rig' }} labelWidth={115} controlMaxWidth={340}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size='small'
                    options={rigDropDowns || []}
                    value={r.rig}
                    onChange={(_, next) => {
                      updateRow(idx, { rig: uniq(next) })
                    }}
                    sx={{
                      ...autoCompleteSx,
                      minWidth: 200,
                    }}
                    slotProps={autoCompleteSlotProps}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </DataConfigFormItem>

                {showMin ? (
                  <DataConfigFormItem item={{ label: 'Minimum' }} labelWidth={115} controlMaxWidth={340}>
                    <TextField
                      fullWidth
                      size='small'
                      type='number'
                      value={numberWithCommasDecimals(r.min, 2)}
                      inputProps={{ step: '0.01' }}
                      onChange={(e) => {
                        updateRow(idx, { min: e.target.value })
                      }}
                      sx={inputSx}
                    />
                  </DataConfigFormItem>
                ) : null}

                {showMax ? (
                  <DataConfigFormItem item={{ label: 'Maximum' }} labelWidth={115} controlMaxWidth={340}>
                    <TextField
                      fullWidth
                      size='small'
                      type='number'
                      value={numberWithCommasDecimals(r.max, 2)}
                      inputProps={{ step: '0.01' }}
                      onChange={(e) => {
                        updateRow(idx, { max: e.target.value })
                      }}
                      sx={inputSx}
                    />
                  </DataConfigFormItem>
                ) : null}
              </Box>

              {shouldShowDepths ? (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
                      my: 0.5,
                    }}
                  />

                  <GlassTitle title='Depth Limits' ui={ui} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1,
                      width: '100%',
                    }}>
                    <DataConfigFormItem item={{ label: 'Min Depth' }} labelWidth={115} controlMaxWidth={340}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}>
                        <Checkbox
                          size='small'
                          checked={r.useMinDepth}
                          onChange={(e) => {
                            updateRow(idx, {
                              useMinDepth: e.target.checked,
                            })
                          }}
                          sx={{
                            p: 0.25,
                            color: ui.defaultTextColor,
                          }}
                        />

                        <TextField
                          fullWidth
                          size='small'
                          type='number'
                          value={r.minDepth}
                          disabled={!r.useMinDepth}
                          onChange={(e) => {
                            updateRow(idx, {
                              minDepth: e.target.value,
                            })
                          }}
                          sx={inputSx}
                        />
                      </Box>
                    </DataConfigFormItem>

                    <DataConfigFormItem item={{ label: 'Max Depth' }} labelWidth={115} controlMaxWidth={340}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}>
                        <Checkbox
                          size='small'
                          checked={r.useMaxDepth}
                          onChange={(e) => {
                            updateRow(idx, {
                              useMaxDepth: e.target.checked,
                            })
                          }}
                          sx={{
                            p: 0.25,
                            color: ui.defaultTextColor,
                          }}
                        />

                        <TextField
                          fullWidth
                          size='small'
                          type='number'
                          value={r.maxDepth}
                          disabled={!r.useMaxDepth}
                          onChange={(e) => {
                            updateRow(idx, {
                              maxDepth: e.target.value,
                            })
                          }}
                          sx={inputSx}
                        />
                      </Box>
                    </DataConfigFormItem>
                  </Box>
                </>
              ) : null}

              <Box
                sx={{
                  width: '100%',
                  borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
                  my: 0.5,
                }}
              />

              <DataConfigFormItem item={{ label: 'Silence Alert' }} labelWidth={160} controlMaxWidth={340}>
                <Checkbox
                  size='small'
                  checked={r.silenceAlert}
                  onChange={(e) => {
                    updateRow(idx, {
                      silenceAlert: e.target.checked,
                    })
                  }}
                  sx={{
                    p: 0.25,
                    color: ui.defaultTextColor,
                  }}
                />
              </DataConfigFormItem>

              {showProjections ? (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
                      my: 0.5,
                    }}
                  />

                  <DataConfigFormItem item={{ label: 'Include Projections' }} labelWidth={160} controlMaxWidth={340}>
                    <Checkbox
                      size='small'
                      checked={!r.ignoreProjections}
                      onChange={(e) => {
                        updateRow(idx, {
                          ignoreProjections: !e.target.checked,
                        })
                      }}
                      sx={{
                        p: 0.25,
                        color: ui.defaultTextColor,
                      }}
                    />
                  </DataConfigFormItem>
                </>
              ) : null}
            </Box>
          )
        })}
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: getWindowBarColor(),
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Cancel
        </Button>

        <Button variant='outlined' color='primary' onClick={handleSubmit} disabled={!canSubmit}>
          {alertToEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddAlertDialog
